import classNames from 'classnames';

import React, {
  createElement,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useMemo,
} from 'react';

import overrideStyles from './overrides.scss';
import type { Tag as TagVariant } from './types';
import styles from './typography.scss';

type AttributeTypes = HTMLSpanElement & HTMLParagraphElement & HTMLHeadingElement;

type TagProps = RefAttributes<AttributeTypes> &
  HTMLAttributes<AttributeTypes> & {
    tag: TagVariant;
    variant: string;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-typography';
const ALLOWED_TAGS = ['span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'];

const Tag: ForwardRefExoticComponent<TagProps> = forwardRef<AttributeTypes, TagProps>(
  ({ tag = 'span', variant, dataTestId, className, children, ...rest }, ref) => {
    const classes = classNames(styles.tag, variant, overrideStyles[variant], className);

    const elementType = useMemo(() => (ALLOWED_TAGS.includes(tag) ? tag : 'span'), [tag]);

    return createElement(
      elementType,
      {
        'data-testid': dataTestId,
        'data-id': dataTestId,
        'data-component-name': COMPONENT_ID,
        className: classes,
        ref,
        ...rest,
      },
      children,
    );
  },
);

Tag.displayName = 'Tag';

export type TypographyProps = RefAttributes<AttributeTypes> &
  HTMLAttributes<AttributeTypes> & {
    variant: string;
    tag?: TagVariant;
    dataTestId?: string;
  };

export const Typography: ForwardRefExoticComponent<TypographyProps> = forwardRef<
  AttributeTypes,
  TypographyProps
>(({ variant, tag = 'span', dataTestId = COMPONENT_ID, children, ...rest }, ref) => (
  <Tag tag={tag} variant={variant} dataTestId={dataTestId} ref={ref} {...rest}>
    {children}
  </Tag>
));

Typography.displayName = 'Typography';
